// HM-312.r cluster 15 visual rework + cluster 15.5 (HM-323) refactor:
// griglia camere SPEC mockup v3 §rooms-card.
// Layout:
//   - rooms-card bianca padding 22 borderRadius 12 border 1px
//   - per gruppo (building / floor): floor-label uppercase tracked +
//     grid repeat(7, 1fr) gap 12
//
// Il FILTRO viene applicato dall'esterno tramite la prop `filter`:
// 'all' = mostra tutte, RoomStatus = mostra solo quelle. La pill row
// interna del cluster 15.3 e' stata rimossa per evitare doppia UI.
// Cluster 15.5 wira la prop tramite useRoomFilterStore + RoomFilterBar.

import type { BookingDto, RoomDb } from '../../api/rooms.js';
import { RoomTile } from './RoomTile.js';

type RoomStatus = RoomDb['status'];

interface Props {
  rooms: RoomDb[];
  bookings: BookingDto[];
  now: Date;
  selectedRoomId: string | null;
  onSelect: (room: RoomDb) => void;
  /** 'all' (default) = mostra tutte. RoomStatus = mostra solo quelle. */
  filter?: 'all' | RoomStatus;
  /** HM-473 audit A7: roomId che stanno lampeggiando per codice uscita usato. */
  blinkingExitRoomIds?: Set<string>;
}

interface Group {
  buildingLabel: string;
  floor: string;
  rooms: RoomDb[];
}

// HM-FIX-16.3 BUG3: importato dal helper centralizzato
import { buildingLabelOf } from '../../lib/buildingLabel.js';

function groupKey(b: string): string {
  return buildingLabelOf(b);
}

function buildGroups(rooms: RoomDb[]): Group[] {
  const map = new Map<string, RoomDb[]>();
  for (const r of rooms) {
    const k = `${r.building}|${r.floor}`;
    const arr = map.get(k) ?? [];
    arr.push(r);
    map.set(k, arr);
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, list]) => {
      const [building = '', floor = ''] = k.split('|');
      return {
        buildingLabel: groupKey(building),
        floor,
        rooms: [...list].sort((a, b) => a.code.localeCompare(b.code)),
      };
    });
}

export function RoomGrid({
  rooms,
  bookings,
  now,
  selectedRoomId,
  onSelect,
  filter = 'all',
  blinkingExitRoomIds,
}: Props) {
  const groups = buildGroups(rooms);

  return (
    <section
      data-testid="room-grid-v2"
      // HM-600 iPad UX: densita' compatta <=1280 (max-xl) per far stare piu'
      // camere senza scroll in landscape; desktop >=xl invariato.
      className="bg-bg-card border border-border-base rounded-xl p-[22px] max-xl:p-3"
    >
      {groups.map((g, idx) => (
        <div key={`${g.buildingLabel}-${g.floor}`} className={idx > 0 ? 'mt-6 max-xl:mt-4' : ''}>
          <div className="text-[11px] font-semibold text-text-muted uppercase tracking-[0.8px] mb-3.5 max-xl:mb-2">
            {g.buildingLabel} · Piano {g.floor}
          </div>
          {/* Adattamento tablet 2026-07-22: colonne FLUIDE (auto-fill) al posto
              delle 7 fisse — le tile riempiono lo spazio disponibile e sui
              tablet grandi diventano ~170-200px invece di ~125px. auto-fill
              (non auto-fit): un piano con 1 sola camera (es. 301) resta una
              tile normale, non un nastro a tutta larghezza. */}
          <div className="grid gap-3 max-xl:gap-2 grid-cols-[repeat(auto-fill,minmax(150px,1fr))] xl:grid-cols-[repeat(auto-fill,minmax(172px,1fr))]">
            {g.rooms.map((room) => {
              const hidden = filter !== 'all' && room.status !== filter;
              if (hidden) return null;
              return (
                <RoomTile
                  key={room.id}
                  room={room}
                  bookings={bookings.filter((b) => b.roomId === room.id)}
                  now={now}
                  selected={selectedRoomId === room.id}
                  onSelect={onSelect}
                  blinkingExit={blinkingExitRoomIds?.has(room.id) ?? false}
                />
              );
            })}
          </div>
        </div>
      ))}
    </section>
  );
}
